import { Children, Fragment, memo } from 'react';
import { Navigation } from 'swiper';
import { SwiperSlide } from 'swiper/react';
import type { Maybe } from 'yup';

import type { ResponsiveSpaceType } from '@/components/Box';
import type { ContentfulGridFragment } from '@/graphql/contentful/api';
import type { ContentfulData } from '@/integrations/contentful';
import {
  type CmsExtraDataRecord,
  generateSectionKey,
} from '@/integrations/contentful';
import type { WithChildren } from '@/lib/types';
import { getBreakpointSize, theme } from '@/theme';
import type { BreakpointsKeys } from '@/theme/types';

import { renderGridItem } from './cms';
import { parseColNumber, parseMaxWidth } from './helper';

import * as S from './Grid.styles';

export type GridProps = ContentfulGridFragment & {
  draftMode?: boolean;
  extraDataRecord?: CmsExtraDataRecord;
};

const defaultCol = 1;
const defaultColForMobile = 1;
const defaultColGap = 2;
const defaultRowGap = 2;
const defaultPaddingBottom = 0;

export const Grid = ({
  backgroundColor,
  col = '3',
  colForMobile = '1',
  colGap = 'Default',
  contentAlignment = 'Left',
  contentCollection,
  draftMode,
  extraDataRecord,
  id,
  isSlider,
  maxWidth = 'lg',
  paddingBottom = 'Default',
  paddingTop = 'Default',
  reverseDisplayOrderForMobile = false,
  rowGap = 'Default',
  visibleInDesktop,
  visibleInMobile,
}: GridProps) => {
  if ((contentCollection?.items.length ?? 0) < 1) {
    return null;
  }

  const colGapNum = parseColNumber(colGap ?? '', defaultColGap);
  const rowGapNum = parseColNumber(rowGap ?? '', defaultRowGap);
  const colNum = parseColNumber(col ?? '', defaultCol);
  const colForMobileNum = parseColNumber(
    colForMobile ?? '',
    defaultColForMobile,
  );

  const paddingBottomNum = parseColNumber(
    paddingBottom ?? '',
    defaultPaddingBottom,
  ) as ResponsiveSpaceType;
  const paddingTopNum = parseColNumber(
    paddingTop ?? '',
    defaultPaddingBottom,
  ) as ResponsiveSpaceType;
  const finalMaxWidth = parseMaxWidth(maxWidth);

  const cmsData = contentCollection?.items.filter((item) => !!item) ?? [];

  const finalGridRowGap = Math.ceil(Number(rowGapNum)) as ResponsiveSpaceType;
  const finalColNum = Number(colNum);
  const finalColForMobileNum = Number(colForMobileNum);
  const finalGridColumnGap = Math.ceil(
    Number(colGapNum),
  ) as ResponsiveSpaceType;

  return (
    <>
      {id ? (
        <div id={id}>
          <RenderGridComponent
            backgroundColor={backgroundColor?.value}
            cmsData={cmsData as ContentfulData[]}
            col={finalColNum}
            colForMobile={finalColForMobileNum}
            contentAlignment={contentAlignment}
            disableGutters={maxWidth === 'full'}
            draftMode={draftMode}
            extraDataRecord={extraDataRecord}
            full={maxWidth === 'full'}
            gridColumnGap={finalGridColumnGap}
            gridRowGap={finalGridRowGap}
            isSlider={!!isSlider}
            maxWidth={finalMaxWidth as BreakpointsKeys}
            paddingBottom={paddingBottomNum}
            paddingTop={paddingTopNum}
            reverseDisplayOrderForMobile={reverseDisplayOrderForMobile || false}
            visibleInDesktop={visibleInDesktop}
            visibleInMobile={visibleInMobile}
          />
        </div>
      ) : (
        <RenderGridComponent
          backgroundColor={backgroundColor?.value}
          cmsData={cmsData as ContentfulData[]}
          col={finalColNum}
          colForMobile={finalColForMobileNum}
          contentAlignment={contentAlignment}
          disableGutters={maxWidth === 'full'}
          draftMode={draftMode}
          extraDataRecord={extraDataRecord}
          full={maxWidth === 'full'}
          gridColumnGap={finalGridColumnGap}
          gridRowGap={finalGridRowGap}
          isSlider={!!isSlider}
          maxWidth={finalMaxWidth as BreakpointsKeys}
          paddingBottom={paddingBottomNum}
          paddingTop={paddingTopNum}
          reverseDisplayOrderForMobile={reverseDisplayOrderForMobile || false}
          visibleInDesktop={visibleInDesktop}
          visibleInMobile={visibleInMobile}
        />
      )}
    </>
  );
};

interface RenderGridComponentProps {
  backgroundColor?: string;
  cmsData: ContentfulData[];
  col: number;
  colForMobile: number;
  contentAlignment?: string | null;
  disableGutters?: boolean;
  draftMode?: boolean;
  extraDataRecord?: CmsExtraDataRecord;
  full: boolean;
  gridColumnGap?: ResponsiveSpaceType;
  gridRowGap?: ResponsiveSpaceType;
  isSlider?: boolean;
  maxWidth?: BreakpointsKeys;
  paddingBottom?: ResponsiveSpaceType;
  paddingTop?: ResponsiveSpaceType;
  reverseDisplayOrderForMobile?: boolean;
  visibleInDesktop?: Maybe<boolean>;
  visibleInMobile?: Maybe<boolean>;
}

const RenderGridComponent = memo(
  function RenderGridComponent(props: RenderGridComponentProps) {
    const {
      backgroundColor,
      cmsData,
      col,
      colForMobile,
      contentAlignment,
      disableGutters,
      draftMode,
      extraDataRecord,
      full,
      gridColumnGap,
      gridRowGap,
      isSlider,
      maxWidth,
      paddingBottom,
      paddingTop,
      reverseDisplayOrderForMobile,
      visibleInDesktop,
      visibleInMobile,
    } = props;
    const RenderGrid = ({ children }: WithChildren) => {
      const itemsCount = Children.count(children);

      return (
        <S.StyledGrid
          $backgroundColor={backgroundColor}
          $col={Math.ceil(col)}
          $colForMobile={Math.ceil(colForMobile)}
          $contentAlignment={contentAlignment}
          $itemsCount={itemsCount}
          $reverseForMobile={reverseDisplayOrderForMobile || false}
          gridColumnGap={gridColumnGap}
          gridRowGap={gridRowGap}
        >
          {children}
        </S.StyledGrid>
      );
    };
    const RenderSlider = ({ children }: WithChildren) => {
      const slidesPerView = col;
      const itemsCount = Children.count(children);
      const spaceBetween = theme.spacing(Number(gridColumnGap));
      const breakpoints = {
        [getBreakpointSize('md')]: {
          slidesPerView: slidesPerView,
          spaceBetween: spaceBetween,
        },
        [getBreakpointSize('lg')]: {
          slidesPerView: slidesPerView,
          spaceBetween: spaceBetween,
        },
      };
      return (
        <S.StyledSwiper
          $backgroundColor={backgroundColor}
          $hideNavigation={slidesPerView >= itemsCount}
          breakpoints={breakpoints}
          centerInsufficientSlides
          centeredSlidesBounds
          modules={[Navigation]}
          navigation
          separator='false'
          slidesPerView={colForMobile}
          spaceBetween={spaceBetween}
          watchSlidesProgress
        >
          {Children.map(
            children,
            (child, index) => (
              // prettier-ignore
              <SwiperSlide key={index}>{child}</SwiperSlide>
            ), //NOSONAR
          )}
        </S.StyledSwiper>
      );
    };

    const RenderFactory = ({ children }: WithChildren) => {
      if (isSlider) {
        return <RenderSlider>{children}</RenderSlider>;
      }

      return <RenderGrid>{children}</RenderGrid>;
    };
    return (
      <S.Wrapper
        $visibleInDesktop={visibleInDesktop}
        $visibleInMobile={visibleInMobile}
        disableGutters={disableGutters}
        full={full}
        maxWidth={maxWidth}
        paddingBottom={paddingBottom}
        paddingTop={paddingTop}
      >
        <RenderFactory>
          {cmsData.map((item, i) => (
            <Fragment key={generateSectionKey(item, i)}>
              {renderGridItem(item, extraDataRecord, draftMode)}
            </Fragment>
          ))}
        </RenderFactory>
      </S.Wrapper>
    );
  },
  (prevProps, nextProps) => {
    return JSON.stringify(prevProps) === JSON.stringify(nextProps);
  },
);
